// apps/console/src/services/api/robot-alerts-store.ts
//
// React-free pub/sub for currently-OPEN robot alerts (Robot Fleet Alerts
// Phase 2). Mirrors insufficient-credits-emitter.ts: a module-level listener
// Set plus a module-level snapshot array of the open alerts. Fed by three
// sources — the live channel (useLiveChannel's alert branch + the kind:'alert'
// snapshot) and the 5s database poll (useRobotAlertsPolling) — and read by
// useRobotAlerts(). Holds ONLY open alerts — ack/resolve removes them
// optimistically; a non-open alert is never added.

import {
  __resetPendingRemovalsForTests,
  dropPendingRemoval,
  holdPendingSighting,
  isRemovalPending,
} from './robot-alert-pending'
import { byRecency, overlayDefined } from './robot-alerts-merge'
import type { RobotAlertItem } from './robot-alerts-types'

export type { RobotAlertItem } from './robot-alerts-types'

type Listener = (alerts: RobotAlertItem[]) => void

/**
 * A listener for the ARRIVAL of an alert, as distinct from a change to the held list.
 *
 * The list listeners replay the WHOLE open-alert array on every change — what a list view wants,
 * and what an interrupting surface cannot use: given "here are four open alerts" there is no way to
 * tell which of them, if any, just happened. An emergency modal must fire on the transition, never
 * on the state, so the transition is published separately instead of diffed at each subscriber.
 */
type ArrivalListener = (item: RobotAlertItem) => void

const listeners = new Set<Listener>()
const arrivalListeners = new Set<ArrivalListener>()
let open: RobotAlertItem[] = []

/**
 * When each held alert entered the store, and when each removed alert left it.
 *
 * Both exist for the same reason: the 5s database poll reads a snapshot of the world at some
 * earlier instant, and applying it wholesale lets a stale read undo something that happened after
 * it. Without arrival times a poll erases an alert pushed live half a second ago; without
 * tombstones it resurrects one the operator has just acknowledged. Neither is acceptable in an
 * alert list — the first hides an emergency, the second cries wolf about a handled one.
 */
const arrivedAt = new Map<string, number>()
const removedAt = new Map<string, number>()

/**
 * How long a removal is remembered.
 *
 * Only needs to outlive a request that was already in flight when the removal happened. A minute is
 * far beyond any poll round-trip and short enough that a genuinely re-opened alert reappears.
 */
export const REMOVAL_TOMBSTONE_MS = 60_000

function forget(now: number): void {
  for (const [id, at] of removedAt) if (now - at > REMOVAL_TOMBSTONE_MS) removedAt.delete(id)
}

/**
 * Alert ids already published as an ARRIVAL this session, so one incident interrupts an operator
 * once.
 *
 * The same `alertId` legitimately reaches this store many times — the page-load snapshot, then the
 * 5s poll, then a live push, all within seconds of a load; presence-dedup re-publishing a still-open
 * incident; a reconnect replaying what is open — and every one after the first is an update to an
 * emergency this operator was already asked about. Re-announcing them would re-open a full-screen
 * modal every few seconds, which trains people to click through the one surface that must never be
 * clicked through.
 *
 * Per-session by design: module state, so it lives exactly as long as the page. A reload is a new
 * session and is asked again (see {@link subscribeRobotAlertArrivals}); nothing but the test reset
 * clears it wholesale, and {@link pruneAnnounced} drops a mark only for an alert that has genuinely
 * gone.
 */
const announced = new Set<string>()

/**
 * Drop announce-marks for incidents that are neither held nor recently removed.
 *
 * A still-open alert stays held across every snapshot and poll, so its mark survives them — which
 * is why a refetch cannot re-announce it. The removal tombstone keeps an ANSWERED alert suppressed:
 * a re-publish already in flight when the operator acknowledged must not interrupt them again. Past
 * that window an alert showing up once more is genuinely back, and is announced again.
 */
function pruneAnnounced(now: number): void {
  const held = new Set(open.map(a => a.alertId))

  for (const id of announced) {
    // A close still waiting for its reply has not happened yet; a refusal puts the alert back.
    if (held.has(id) || isRemovalPending(id)) continue

    const removed = removedAt.get(id)

    if (removed === undefined || now - removed > REMOVAL_TOMBSTONE_MS) announced.delete(id)
  }
}

/**
 * Which of `incoming` are ARRIVALS. Decided BEFORE the write, while the held list and the
 * tombstones still describe the world as it was — afterwards every id looks held, and a live push
 * will have cleared the very tombstone this needed to see.
 *
 * Not-held rejects a re-publish of an incident already in the inbox (presence-dedup, a declined
 * alarm the device keeps reporting, the poll re-listing what the snapshot seeded); not-announced
 * rejects one that raced the operator's acknowledgement, which leaves the alert un-held but very
 * much already answered. One test for every feed, so a snapshot, a poll and a push announce the
 * same alert exactly once between them.
 */
function arrivalsAmong(incoming: RobotAlertItem[], now: number): RobotAlertItem[] {
  pruneAnnounced(now)

  const held = new Set(open.map(a => a.alertId))

  return incoming.filter(a => !announced.has(a.alertId) && !held.has(a.alertId))
}

/**
 * Publish arrivals. Called AFTER notify(), so an arrival listener that also reads the held list
 * sees the alert in it. Each is marked immediately before its listeners run — the same shape for
 * every feed, so a listener cannot tell a seeded or polled arrival from a pushed one.
 */
function announce(arrivals: RobotAlertItem[]): void {
  for (const item of arrivals) {
    announced.add(item.alertId)
    arrivalListeners.forEach(l => l(item))
  }
}

/** Drop keys for alerts that are no longer held, so neither map grows without bound. */
function pruneArrivals(): void {
  const held = new Set(open.map(a => a.alertId))

  for (const id of arrivedAt.keys()) if (!held.has(id)) arrivedAt.delete(id)
}

function notify(): void {
  // Hand each subscriber a fresh array reference so React state setters see a
  // changed identity and re-render.
  const snapshot = open.slice()
  listeners.forEach(l => l(snapshot))
}

/** Upsert a single live alert (newest wins, kept newest-first). No-op for a
 *  non-open alert. Called from useLiveChannel's alert branch. */
export function emitRobotAlert(item: RobotAlertItem, now: number = Date.now()): void {
  if (item.status !== 'open') return
  // The operator just closed it and the server has not replied: the device still reporting it is
  // not news, and showing it would undo the click.
  if (holdPendingSighting(item)) return

  const arrivals = arrivalsAmong([item], now)

  // A live push is authoritative and clears any tombstone: the alert demonstrably exists again.
  removedAt.delete(item.alertId)
  arrivedAt.set(item.alertId, now)
  // Overlay, never replace: a live repeat carries lastSeenAt + sightingCount but no `sightings`.
  open = [
    overlayDefined(
      open.find(a => a.alertId === item.alertId),
      item,
    ),
    ...open.filter(a => a.alertId !== item.alertId),
  ]
  notify()
  announce(arrivals)
}

/**
 * Fold an alert SNAPSHOT into the held list: ADD what is new, REFRESH what is held, REMOVE nothing.
 *
 * A snapshot is not the whole population — the broker caps the fleet alert snapshot at
 * `ALERT_SNAPSHOT_LIMIT` (50) rows, and a `robot:{id}:alert` subscribe answers with ONE device's —
 * so treating it as a replace drops every held alert it happens not to mention. On a tenant with
 * more than 50 open alerts the rows beyond the cap were dropped here, {@link pruneAnnounced} then
 * forgot their marks, and the next poll re-announced them: the same emergencies re-interrupting
 * the operator after every reconnect. Removal belongs to {@link reconcileRobotAlerts} (the poll,
 * 200 rows, tombstone-aware) and {@link removeRobotAlert}; a snapshot is a read too, so the
 * tombstone is honoured here exactly as the poll honours it.
 *
 * Every open row this session has not announced is an arrival. On a page load that is the whole
 * backlog — exactly what an operator arriving mid-incident has not been asked about yet (see
 * {@link subscribeRobotAlertArrivals}).
 */
export function seedRobotAlerts(items: RobotAlertItem[], now: number = Date.now()): void {
  forget(now)

  const held = new Map(open.map(a => [a.alertId, a]))
  const fresh = items.filter(a => a.status === 'open' && !removedAt.has(a.alertId) && !isRemovalPending(a.alertId))
  const arrivals = arrivalsAmong(fresh, now)
  const freshIds = new Set(fresh.map(a => a.alertId))
  const refreshed = fresh.map(a => overlayDefined(held.get(a.alertId), a))

  open = [...refreshed, ...open.filter(a => !freshIds.has(a.alertId))].sort(byRecency)
  for (const a of fresh) arrivedAt.set(a.alertId, now)
  notify()
  announce(arrivals)
}

/**
 * Fold a DATABASE READ into the held list without letting it undo anything newer.
 *
 * The poll is the one feed allowed to REMOVE a held row — it lists every open alert (200 rows,
 * against the snapshot's 50), so an older row it omits is genuinely gone. But a poll response can
 * be stale, in three ways this function handles:
 *
 *  - it may not mention an alert that was pushed live AFTER the read began — kept, not deleted;
 *  - it may still contain one the operator acknowledged after the read began — dropped, not
 *    resurrected;
 *  - it is THINNER than a live row. `name` is not a field on the Mongo `Alert` schema at all — it
 *    rides the live frame only — so a blind replace strips the operator-visible label off every row
 *    in the inbox. Server values win where present; locally-known extras survive where not.
 *
 * An open row the read is allowed to add, and this session has not announced, is an arrival: the
 * poll is how an emergency reaches the operator when the socket missed the push.
 */
export function reconcileRobotAlerts(items: RobotAlertItem[], readStartedAt: number, now: number = Date.now()): void {
  forget(now)

  const held = new Map(open.map(a => [a.alertId, a]))
  const server = items.filter(a => a.status === 'open')
  const serverIds = new Set(server.map(a => a.alertId))

  const reconciled = server
    // Removed after this read began → the read predates the removal and must not undo it. A close
    // still waiting for its reply blocks every read: the server lists it until that reply.
    .filter(a => (removedAt.get(a.alertId) ?? -Infinity) < readStartedAt && !isRemovalPending(a.alertId))
    .map(a => overlayDefined(held.get(a.alertId), a))

  // Held rows the server did not mention. Keep only those that arrived after the read began; an
  // older one the server no longer lists is genuinely gone (resolved elsewhere, or by someone else).
  const newerThanRead = open.filter(a => !serverIds.has(a.alertId) && (arrivedAt.get(a.alertId) ?? 0) >= readStartedAt)

  // Only rows that survived the tombstone filter can arrive — one the read must not resurrect is
  // never announced either.
  const arrivals = arrivalsAmong(reconciled, now)

  open = [...reconciled, ...newerThanRead].sort(byRecency)
  for (const a of open) if (!arrivedAt.has(a.alertId)) arrivedAt.set(a.alertId, now)
  pruneArrivals()
  notify()
  announce(arrivals)
}

/** Drop one alert by id (optimistic ack/resolve). */
export function removeRobotAlert(alertId: string, now: number = Date.now()): void {
  // Tombstone FIRST and unconditionally. A poll that was already in flight when the operator hit
  // Ack would otherwise put the row straight back, and the id may legitimately not be held right
  // now (acked from the dashboard element while the inbox list had already dropped it).
  removedAt.set(alertId, now)
  // A close from another surface stands on its own; a pending one must not later put the alert back.
  dropPendingRemoval(alertId)

  const next = open.filter(a => a.alertId !== alertId)

  if (next.length === open.length) return
  open = next
  arrivedAt.delete(alertId)
  notify()
}

/** Set or clear one held alert's `declinedAt`. A replace, not an overlay: a refused decline must take it back. */
export function setRobotAlertDeclinedAt(alertId: string, declinedAt: string | undefined): void {
  const held = open.find(a => a.alertId === alertId)

  if (!held || held.declinedAt === declinedAt) return

  const next: RobotAlertItem = { ...held, declinedAt }

  if (!declinedAt) delete next.declinedAt
  open = open.map(a => (a === held ? next : a))
  notify()
}

/**
 * Subscribe to alert ARRIVALS — one call per open alert per session, never a replay.
 *
 * Every feed announces: a live push, the snapshot that seeds the page, and the 5s database poll.
 * An alert is an arrival the first time THIS session sees it open, whichever of the three carries
 * it, because the rule is about the alert and not the transport: a critical alert that is still
 * OPEN must be put to whoever is in front of the console. An operator who logs in or reloads
 * mid-incident has not been asked yet and must be, however long the alert predates them; and a
 * socket that dropped, backed off or slept through the push must not turn an SOS into a quiet row
 * in the inbox, so the poll raises what the socket missed. Only a CLOSED alert — acknowledged or
 * resolved, hence no longer open — is exempt. Declined is NOT closed: the operator who declined
 * said only that they are not the one taking it, and the next session, theirs or anyone else's, is
 * asked again.
 *
 * What keeps this from nagging is the per-session `announced` set, not the choice of feed. The
 * same alert reaches the store by all three routes within seconds of a load — seed, then poll, then
 * push — and is published once; a still-open alert stays held across every refetch, so its mark
 * survives and a poll cannot re-pop it every five seconds.
 */
export function subscribeRobotAlertArrivals(listener: ArrivalListener): () => void {
  arrivalListeners.add(listener)

  return () => {
    arrivalListeners.delete(listener)
  }
}

/**
 * The open alerts held right now, for a consumer that wants the backlog once rather than a
 * subscription — the emergency queue reads it when a dashboard mounts, to put to the operator
 * whatever arrived while no surface was listening.
 */
export function readRobotAlerts(): RobotAlertItem[] {
  return open.slice()
}

/** Subscribe; immediately receives the current open-alert list. Returns an
 *  unsubscribe fn. */
export function subscribeRobotAlerts(listener: Listener): () => void {
  listeners.add(listener)
  listener(open.slice())

  return () => {
    listeners.delete(listener)
  }
}

/** Test-only reset of the module state. */
export function __resetRobotAlertsForTests(): void {
  listeners.clear()
  arrivalListeners.clear()
  announced.clear()
  open = []
  arrivedAt.clear()
  removedAt.clear()
  __resetPendingRemovalsForTests()
}
